import { useEffect, useState } from "react";
import { PTHeader } from "@/components/lp/pt/PTHeader";
import { PTHero } from "@/components/lp/pt/PTHero";
import { PTJourney } from "@/components/lp/pt/PTJourney";
import { PTPackages } from "@/components/lp/pt/PTPackages";
import { PTEquipment } from "@/components/lp/pt/PTEquipment";
import { PTSpace } from "@/components/lp/pt/PTSpace";
import { PTDirect } from "@/components/lp/pt/PTDirect";
import { PTProcess } from "@/components/lp/pt/PTProcess";
import { PTMonetize } from "@/components/lp/pt/PTMonetize";
import { PTStudioForm } from "@/components/lp/pt/PTStudioForm";
import { Founder } from "@/components/yanre/Founder";
import { WhatsAppFloat } from "@/components/lp/WhatsAppFloat";
import { Marquee } from "@/components/yanre/Marquee";
import { LandingFooter } from "@/components/lp/LandingFooter";
import { useLocale } from "@/i18n/LocaleContext";
import { PROD_BASE, PT_LOCALES, ptsPath, ptsLocaleTag } from "@/i18n";
import { track } from "@/lib/track";

// Personal Trainer Studio landing page — multilingual family (8 locales).
// English is the x-default/base; all copy flows through t("pts.*", EN default).
export default function PersonalTrainerStudio() {
  const { t, locale } = useLocale();
  const [pkg, setPkg] = useState(null);

  // Localized SEO: unique title/description per locale, self-canonical,
  // reciprocal hreflang across the PT family, x-default → English base.
  useEffect(() => {
    const title = t("pts.seo.title", "Personal Trainer Studio — Commercial-Grade Gym Equipment | YANRE FITNESS");
    const desc = t(
      "pts.seo.description",
      "Build your own professional training studio with commercial-grade YANRE equipment, direct from the manufacturer. Smart starter packages, intelligent space planning and factory-backed support."
    );
    document.title = title;
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", desc);
    setMeta("name", "robots", "index, follow");
    setMeta("property", "og:type", "website");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", `${PROD_BASE}${ptsPath(locale)}`);
    setMeta("property", "og:image", `${PROD_BASE}/media/pt-studio-hero-1672.webp`);
    document.head.querySelectorAll('link[data-seo="canonical"], link[data-seo="hreflang"]').forEach((e) => e.remove());
    const addLink = (rel, hreflang, href) => {
      const el = document.createElement("link");
      el.setAttribute("rel", rel);
      if (hreflang) el.setAttribute("hreflang", hreflang);
      el.setAttribute("href", href);
      el.dataset.seo = hreflang ? "hreflang" : "canonical";
      document.head.appendChild(el);
    };
    addLink("canonical", null, `${PROD_BASE}${ptsPath(locale)}`);
    PT_LOCALES.forEach((l) => addLink("alternate", ptsLocaleTag(l), `${PROD_BASE}${ptsPath(l)}`));
    addLink("alternate", "x-default", `${PROD_BASE}${ptsPath("en")}`);
    track("page_view", { page: "personal-trainer-studio", locale });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const selectPackage = (value) => {
    setPkg(value);
    track("cta_start_project", { placement: `package_${value}`, page: "personal-trainer-studio" });
    document.getElementById("studio-project")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-carbon-950 font-sans text-titanium antialiased" data-testid="pt-studio-page">
      <PTHeader />
      <main>
        <PTHero />
        <Marquee
          items={["Precision", "Performance", "Confidence", "Engineering", "Since 1997"]}
          className="border-y border-carbon-700 bg-carbon-950 py-6"
        />
        <PTJourney />
        <PTPackages onSelectPackage={selectPackage} />
        <PTEquipment />
        <PTSpace />
        <PTMonetize />
        <PTDirect />
        <PTProcess />
        <Founder />
        <PTStudioForm packagePreselect={pkg} />
      </main>
      <LandingFooter />
      <WhatsAppFloat page="personal-trainer-studio" />
    </div>
  );
}
